import React, { useState } from 'react';
import { Award, Trophy, Star, Mail, Phone, MapPin, ChevronDown, CheckCircle } from 'lucide-react';
import { Button } from './components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './components/ui/card';
import { Input } from './components/ui/input';
import { Textarea } from './components/ui/textarea';
import { Label } from './components/ui/label';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from './components/ui/accordion';
import { Separator } from './components/ui/separator';
import { toast } from 'sonner';
import { Toaster } from './components/ui/sonner';
import './App.css';

// Formspree endpoint - replace with your actual Formspree form ID after signup
const FORMSPREE_ENDPOINT = 'https://formspree.io/f/mlgwndpz';

function App() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    horseDetails: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch(FORMSPREE_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          horseDetails: formData.horseDetails,
          message: formData.message,
          _subject: 'New Inquiry from Palmer Pastures Website',
        })
      });

      if (response.ok) {
        toast.success('Thank you for your inquiry! We will contact you shortly.');
        setFormData({
          name: '',
          email: '',
          phone: '',
          message: '',
          horseDetails: ''
        });
      } else {
        toast.error('Something went wrong. Please try again or email us directly.');
      }
    } catch (error) {
      toast.error('Unable to send message. Please email us directly at palmer.pastures@gmail.com');
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToContact = () => {
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="App">
      <Toaster position="top-center" richColors />
      
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gold/20 shadow-sm">
        <nav className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Award className="h-8 w-8 text-gold" />
              <div>
                <h1 className="text-2xl font-bold text-burgundy" style={{color: 'hsl(0, 45%, 35%)'}}>Palmer Pastures</h1>
                <p className="text-xs text-muted-foreground">Excellence in Equestrian Training</p>
              </div>
            </div>
            <div className="hidden md:flex items-center space-x-8">
              <a href="#services" className="text-sm font-medium hover:text-gold">Our Approach</a>
              <a href="#about" className="text-sm font-medium hover:text-gold">Why Us</a>
              <a href="#timeline" className="text-sm font-medium hover:text-gold">The Journey</a>
              <Button onClick={scrollToContact} className="bg-burgundy hover:bg-burgundy/90 text-gold">
                Contact Us
              </Button>
            </div>
          </div>
        </nav>
      </header>

      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden mt-16">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1671812933995-023154aca70c)',
            backgroundPosition: 'center 40%'
          }}
        />
        <div className="absolute inset-0 hero-overlay" />
        
        <div className="relative z-10 container mx-auto px-6 text-center text-white">
          <div className="animate-fade-in-up max-w-4xl mx-auto">
            <div className="flex justify-center mb-6">
              <Trophy className="h-16 w-16 text-gold" />
            </div>
            <h2 className="text-5xl md:text-7xl font-bold mb-6 text-gold leading-tight">
              Start Your Horse's Education Right
            </h2>
            <p className="text-xl md:text-2xl mb-8 text-white/95 leading-relaxed max-w-3xl mx-auto">
              Expert first education for young sport horses. Modern, ethical methods that build confidence and lasting foundations.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Button 
                size="lg" 
                onClick={scrollToContact}
                className="bg-gold hover:bg-gold/90 text-burgundy text-lg px-8 py-6 font-semibold shadow-xl"
              >
                Begin The Journey
              </Button>
              <Button 
                size="lg" 
                variant="outline"
                className="border-2 border-gold text-white hover:bg-gold/20 text-lg px-8 py-6 font-semibold"
                onClick={() => document.getElementById('about')?.scrollIntoView({ behavior: 'smooth' })}
              >
                Learn More
              </Button>
            </div>
          </div>
          
          <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
            <ChevronDown className="h-8 w-8 text-gold" />
          </div>
        </div>
      </section>

      {/* Initial Education Section */}
      <section id="services" className="py-24 bg-gradient-to-b from-white to-muted/30">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div className="order-2 md:order-1">
                <h3 className="text-4xl md:text-5xl font-bold mb-6 text-burgundy">First Education for Young Sport Horses</h3>
                <p className="text-lg text-foreground/90 mb-6 leading-relaxed">
                  Palmer Pastures specializes in the critical first education phase for young horses destined for competitive sport. 
                  We provide the foundation that determines your horse's entire career.
                </p>
                <p className="text-lg text-foreground/90 mb-8 leading-relaxed">
                  Using modern, ethical methods, we build confidence, understanding, and correct movement patterns that will serve 
                  your horse throughout their life—whether in Dressage, Show Jumping, or Eventing.
                </p>
                
                <div className="space-y-4 mb-8">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="h-6 w-6 text-gold mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-burgundy mb-1">Patient, Progressive Approach</h4>
                      <p className="text-muted-foreground">Each horse learns at their own pace with methods tailored to their temperament</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="h-6 w-6 text-gold mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-burgundy mb-1">Solid Foundation Skills</h4>
                      <p className="text-muted-foreground">Groundwork, acceptance of tack, mounting, basic gaits, and steering under saddle</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="h-6 w-6 text-gold mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-burgundy mb-1">Confidence Building</h4>
                      <p className="text-muted-foreground">Exposure to varied environments, obstacles, and situations to develop a brave, willing partner</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="order-1 md:order-2">
                <div className="grid grid-rows-2 gap-4">
                  <div className="relative h-64 overflow-hidden rounded-lg shadow-xl">
                    <img 
                      src="https://images.unsplash.com/photo-1671812933995-023154aca70c" 
                      alt="Young Horse Training"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="relative h-64 overflow-hidden rounded-lg shadow-xl">
                    <img 
                      src="https://images.unsplash.com/photo-1758573951452-e720d09c7b2c" 
                      alt="Horse Education"
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-24 bg-white">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            <div className="order-2 md:order-1">
              <h3 className="text-4xl md:text-5xl font-bold mb-6 text-burgundy">Why Palmer Pastures</h3>
              <div className="space-y-6 text-lg text-foreground/90 leading-relaxed">
                <p>
                  First education is the most critical phase in a young horse's life. The experiences and foundations 
                  built during this time shape everything that follows.
                </p>
                <p>
                  We work with horses from renowned breeders and discerning owners who understand the value of getting 
                  it right from the start. Our approach prioritizes the horse's mental and physical wellbeing, building 
                  willing partnerships rather than forced compliance.
                </p>
                <p className="font-semibold text-burgundy">
                  Every horse deserves the best beginning. That's our commitment to you.
                </p>
              </div>
            </div>
            
            <div className="order-1 md:order-2">
              <div className="relative">
                <img 
                  src="https://images.unsplash.com/flagged/photo-1568382007442-2a8c9c1113f7" 
                  alt="Palmer Pastures Training"
                  className="rounded-lg shadow-2xl w-full h-auto"
                />
                <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-gold/20 rounded-lg -z-10" />
                <div className="absolute -top-6 -right-6 w-32 h-32 bg-burgundy/20 rounded-lg -z-10" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 bg-gradient-to-b from-muted/30 to-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h3 className="text-4xl md:text-5xl font-bold mb-4 text-burgundy">Begin The Journey</h3>
              <p className="text-lg text-muted-foreground">
                Let's discuss your young horse's first education
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-12">
              <div>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <Label htmlFor="name">Name *</Label>
                    <Input 
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="mt-2"
                      placeholder="Your full name"
                    />
                  </div>

                  <div>
                    <Label htmlFor="email">Email *</Label>
                    <Input 
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="mt-2"
                      placeholder="your.email@example.com"
                    />
                  </div>

                  <div>
                    <Label htmlFor="phone">Phone</Label>
                    <Input 
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="mt-2"
                      placeholder="Your contact number"
                    />
                  </div>

                  <div>
                    <Label htmlFor="horseDetails">Horse Details</Label>
                    <Textarea 
                      id="horseDetails"
                      name="horseDetails"
                      value={formData.horseDetails}
                      onChange={handleInputChange}
                      className="mt-2 min-h-[80px]"
                      placeholder="Age, breed, current level, discipline interest"
                    />
                  </div>

                  <div>
                    <Label htmlFor="message">Message *</Label>
                    <Textarea 
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                      className="mt-2 min-h-[100px]"
                      placeholder="Tell us about your training goals"
                    />
                  </div>

                  <Button 
                    type="submit" 
                    disabled={isSubmitting}
                    className="w-full bg-burgundy hover:bg-burgundy/90 text-gold text-lg py-6"
                  >
                    {isSubmitting ? 'Sending...' : 'Send Inquiry'}
                  </Button>
                </form>
              </div>

              <div className="space-y-8">
                <div>
                  <h4 className="text-2xl font-bold mb-6 text-burgundy">Contact Information</h4>
                  
                  <div className="space-y-6">
                    <div className="flex items-start gap-4">
                      <Mail className="h-6 w-6 text-gold mt-1 flex-shrink-0" />
                      <div>
                        <p className="font-semibold mb-1">Email</p>
                        <a href="mailto:palmer.pastures@gmail.com" className="text-muted-foreground hover:text-gold">
                          palmer.pastures@gmail.com
                        </a>
                      </div>
                    </div>

                    <div className="flex items-start gap-4">
                      <Phone className="h-6 w-6 text-gold mt-1 flex-shrink-0" />
                      <div>
                        <p className="font-semibold mb-1">Phone</p>
                        <p className="text-muted-foreground">0407 511 253</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-4">
                      <MapPin className="h-6 w-6 text-gold mt-1 flex-shrink-0" />
                      <div>
                        <p className="font-semibold mb-1">Location</p>
                        <p className="text-muted-foreground">62 James Rd, MIDDLE SWAN WA<br/>Australia</p>
                      </div>
                    </div>
                  </div>
                </div>

                <Separator />

                <div className="bg-muted/50 p-6 rounded-lg border-2 border-gold/20">
                  <h5 className="font-bold text-burgundy mb-3">What to Expect</h5>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-gold mt-0.5 flex-shrink-0" />
                      <span>Response within 24 hours</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-gold mt-0.5 flex-shrink-0" />
                      <span>Customized training proposal</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-gold mt-0.5 flex-shrink-0" />
                      <span>Transparent pricing and timeline</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What to Expect Timeline Section */}
      <section id="timeline" className="py-24 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-16">
              <h3 className="text-4xl md:text-5xl font-bold mb-4 text-burgundy">The First Education Journey</h3>
              <p className="text-lg text-muted-foreground">
                A progressive, patient approach tailored to your horse's individual needs
              </p>
            </div>

            <div className="relative">
              {/* Timeline line */}
              <div className="absolute left-8 md:left-1/2 top-0 bottom-0 w-0.5 bg-gold/30 md:transform md:-translate-x-1/2"></div>

              {/* Timeline items */}
              <div className="space-y-12">
                {/* Week 1-2 */}
                <div className="relative flex flex-col md:flex-row md:items-center gap-8">
                  <div className="md:w-1/2 md:text-right md:pr-12 order-2 md:order-1">
                    <div className="bg-muted/50 p-6 rounded-lg border-2 border-gold/20 hover:border-gold transition-all">
                      <h4 className="text-2xl font-bold text-burgundy mb-3">Week 1-2: Foundation & Trust</h4>
                      <p className="text-muted-foreground leading-relaxed">
                        Building relationship and establishing basic groundwork. Leading, grooming, handling feet, 
                        introduction to training environment. Focus on confidence and trust building.
                      </p>
                    </div>
                  </div>
                  <div className="absolute left-8 md:left-1/2 w-4 h-4 bg-gold rounded-full border-4 border-white shadow-lg md:transform md:-translate-x-1/2"></div>
                  <div className="md:w-1/2 order-1 md:order-2"></div>
                </div>

                {/* Week 3-4 */}
                <div className="relative flex flex-col md:flex-row md:items-center gap-8">
                  <div className="md:w-1/2 order-1"></div>
                  <div className="absolute left-8 md:left-1/2 w-4 h-4 bg-gold rounded-full border-4 border-white shadow-lg md:transform md:-translate-x-1/2"></div>
                  <div className="md:w-1/2 md:pl-12 order-2">
                    <div className="bg-muted/50 p-6 rounded-lg border-2 border-gold/20 hover:border-gold transition-all">
                      <h4 className="text-2xl font-bold text-burgundy mb-3">Week 3-4: Introduction to Tack</h4>
                      <p className="text-muted-foreground leading-relaxed">
                        Gradual introduction to saddle, bridle, and bit. Longeing with tack. Building comfort 
                        and acceptance of equipment. Continued groundwork refinement.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Week 5-6 */}
                <div className="relative flex flex-col md:flex-row md:items-center gap-8">
                  <div className="md:w-1/2 md:text-right md:pr-12 order-2 md:order-1">
                    <div className="bg-muted/50 p-6 rounded-lg border-2 border-gold/20 hover:border-gold transition-all">
                      <h4 className="text-2xl font-bold text-burgundy mb-3">Week 5-6: First Mounting & Walking</h4>
                      <p className="text-muted-foreground leading-relaxed">
                        Introduction to mounting and carrying a rider. Walking exercises, steering basics, 
                        halts and transitions. All work done calmly and at the horse's pace.
                      </p>
                    </div>
                  </div>
                  <div className="absolute left-8 md:left-1/2 w-4 h-4 bg-gold rounded-full border-4 border-white shadow-lg md:transform md:-translate-x-1/2"></div>
                  <div className="md:w-1/2 order-1 md:order-2"></div>
                </div>

                {/* Week 7-8 */}
                <div className="relative flex flex-col md:flex-row md:items-center gap-8">
                  <div className="md:w-1/2 order-1"></div>
                  <div className="absolute left-8 md:left-1/2 w-4 h-4 bg-gold rounded-full border-4 border-white shadow-lg md:transform md:-translate-x-1/2"></div>
                  <div className="md:w-1/2 md:pl-12 order-2">
                    <div className="bg-muted/50 p-6 rounded-lg border-2 border-gold/20 hover:border-gold transition-all">
                      <h4 className="text-2xl font-bold text-burgundy mb-3">Week 7-8: Building Gaits</h4>
                      <p className="text-muted-foreground leading-relaxed">
                        Introduction to trot under saddle. Improving balance, rhythm, and responsiveness. 
                        Continued confidence building with varied environments and obstacles.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Week 9-12 */}
                <div className="relative flex flex-col md:flex-row md:items-center gap-8">
                  <div className="md:w-1/2 md:text-right md:pr-12 order-2 md:order-1">
                    <div className="bg-muted/50 p-6 rounded-lg border-2 border-gold/20 hover:border-gold transition-all">
                      <h4 className="text-2xl font-bold text-burgundy mb-3">Week 9-12: Refinement & Confidence</h4>
                      <p className="text-muted-foreground leading-relaxed">
                        Refining all basics, adding canter work when ready. Exposure to different locations, 
                        arena work, trail riding. Horse ready for next phase of specialized training.
                      </p>
                    </div>
                  </div>
                  <div className="absolute left-8 md:left-1/2 w-4 h-4 bg-gold rounded-full border-4 border-white shadow-lg md:transform md:-translate-x-1/2"></div>
                  <div className="md:w-1/2 order-1 md:order-2"></div>
                </div>
              </div>
            </div>

            <div className="mt-12 text-center bg-muted/30 p-8 rounded-lg border border-gold/20">
              <p className="text-lg text-foreground/90 mb-2">
                <strong className="text-burgundy">Every horse is unique.</strong> This timeline is a guide—we adjust 
                pace and approach based on your horse's individual temperament, physical development, and learning style.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-burgundy text-white py-12">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <div className="flex items-center space-x-3 mb-4">
                <Award className="h-8 w-8 text-gold" />
                <h4 className="text-xl font-bold text-gold">Palmer Pastures</h4>
              </div>
              <p className="text-white/80">
                Expert first education for young sport horses.
              </p>
            </div>

            <div>
              <h5 className="font-bold mb-4 text-gold">Quick Links</h5>
              <ul className="space-y-2">
                <li><a href="#services" className="text-white/80 hover:text-gold">Our Approach</a></li>
                <li><a href="#about" className="text-white/80 hover:text-gold">Why Us</a></li>
                <li><a href="#timeline" className="text-white/80 hover:text-gold">The Journey</a></li>
                <li><a href="#contact" className="text-white/80 hover:text-gold">Contact</a></li>
                <li><a href="/terms-and-conditions.pdf" target="_blank" className="text-white/80 hover:text-gold">Terms & Conditions (PDF)</a></li>
              </ul>
            </div>

            <div>
              <h5 className="font-bold mb-4 text-gold">Contact</h5>
              <ul className="space-y-2 text-white/80">
                <li className="flex items-center gap-2">
                  <Mail className="h-4 w-4" />
                  <a href="mailto:palmer.pastures@gmail.com" className="hover:text-gold">
                    palmer.pastures@gmail.com
                  </a>
                </li>
                <li className="flex items-center gap-2">
                  <MapPin className="h-4 w-4" />
                  <span>Australia</span>
                </li>
              </ul>
            </div>
          </div>

          <Separator className="bg-white/20 mb-6" />

          <div className="text-center text-white/70 text-sm">
            <p>&copy; {new Date().getFullYear()} Palmer Pastures Pty Ltd. All rights reserved.</p>
            <p className="mt-2">ABN: [Your ABN] | www.palmerpastures.com.au</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
